import { CSSProperties, useEffect, useMemo, useRef } from 'react'

export interface PrintEmailContentProps {
  /**
   * Raw HTML string — a full, standalone email/print document (it carries its
   * own <style>, `body` and `@page` rules and global selectors).
   */
  html: string
  /** Sets document.title (browser tab + print header). */
  title?: string
  className?: string
  style?: CSSProperties
  /**
   * Page margin applied inside the sheet (default '0'). The padding sits INSIDE
   * the template's A4 body (`box-sizing: border-box`) so the body still maps 1:1
   * onto the printed page — no right-edge clip. Pass e.g. '15mm' for a margin.
   * Only used in the default (non-isolated) render.
   */
  pageMargin?: string
  /**
   * Legacy/no-op: the viewer always sizes to its content (direct render flows
   * naturally; the isolated iframe auto-grows). Accepted so existing call sites
   * can keep passing it.
   */
  autoHeight?: boolean
  /**
   * Legacy/no-op in the default render: Ctrl/Cmd+P already prints the document
   * natively because it IS the page. Accepted so existing call sites can keep
   * passing it.
   */
  capturePrintShortcut?: boolean
  /** Open the browser print dialog automatically once the content is rendered. */
  autoPrint?: boolean
  /**
   * Render inside an isolated <iframe> instead of directly on the page. Use ONLY
   * when embedding the document inside an app screen that has surrounding chrome
   * (e.g. a detail page), where the document's global `body` / `@page` / element
   * styles would otherwise leak onto the host app and break its layout.
   *
   * Standalone full-page print routes must leave this false: rendering the
   * document directly lets the browser print it natively (no iframe element =
   * no on-screen scroll state or pagination quirks bleeding into the output).
   */
  isolated?: boolean
}

// Toggled on <html> only while a direct (non-isolated) document is mounted, so
// the reset below is scoped to these print pages and never touches other
// EmptyLayout routes (video, day-sheet, …).
const HOST_CLASS = 'pec-host'

// Frames the rendered document as a centred "sheet": grey viewport on screen,
// white A4 page centred with a drop shadow, edge-to-edge on paper. The template
// owns its width (`body { width: 210mm }`); we only neutralise the app's global
// styles (Tailwind preflight, body min-height, #root flex/height) and add the
// page margin via `box-sizing: border-box` padding so the body still maps 1:1
// onto the printed page (no clip) — with `@page { margin: 0 }` to match.
const buildHostCss = (pageMargin: string) => `
  html.${HOST_CLASS} {
    background: #e9e9e9;
  }
  html.${HOST_CLASS} body {
    box-sizing: border-box !important;
    margin: 24px auto !important;
    padding: ${pageMargin} !important;
    min-height: 0 !important;
    height: auto !important;
    background: #fff !important;
    box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  }
  html.${HOST_CLASS} #root {
    display: block !important;
    width: auto !important;
    min-height: 0 !important;
    height: auto !important;
  }
  @media print {
    @page {
      margin: 0;
    }
    html.${HOST_CLASS} {
      background: #fff;
    }
    html.${HOST_CLASS} body {
      margin: 0 !important;
      box-shadow: none !important;
    }
  }
`

// Renders the document straight into the page so the browser prints it natively.
// Note: the document's <style> applies to the real document — fine here because
// these are standalone full-page routes where the document IS the page.
const DirectContent = ({
  html,
  title,
  className,
  style,
  pageMargin = '0',
  autoPrint = false,
}: PrintEmailContentProps) => {
  useEffect(() => {
    const root = document.documentElement
    root.classList.add(HOST_CLASS)
    return () => root.classList.remove(HOST_CLASS)
  }, [])

  useEffect(() => {
    if (title) {
      document.title = title
    }
  }, [title])

  useEffect(() => {
    if (!autoPrint || !html) {
      return undefined
    }
    // Let the just-injected content (and the tenant logo image) lay out before
    // opening the dialog.
    const timer = window.setTimeout(() => window.print(), 300)
    return () => window.clearTimeout(timer)
  }, [autoPrint, html])

  const hostCss = useMemo(() => buildHostCss(pageMargin), [pageMargin])

  return (
    <>
      <div
        className={className}
        style={style}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <style>{hostCss}</style>
    </>
  )
}

// Style-isolated render for documents embedded inside an app screen. Keeps the
// document's global `body` / `@page` styles from leaking onto the host app, and
// auto-grows to the content height.
const IsolatedFrame = ({
  html,
  title = 'content',
  className,
  style,
}: PrintEmailContentProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const cleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => () => cleanupRef.current?.(), [])

  const handleLoad = () => {
    const doc = iframeRef.current?.contentDocument
    if (!doc?.body) {
      return
    }
    doc.body.style.margin = '0'
    const resize = () => {
      if (iframeRef.current && doc.documentElement) {
        iframeRef.current.style.height = `${doc.documentElement.scrollHeight}px`
      }
    }
    resize()
    cleanupRef.current?.()
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(resize)
      observer.observe(doc.body)
      cleanupRef.current = () => observer.disconnect()
    }
  }

  return (
    <iframe
      ref={iframeRef}
      title={title}
      srcDoc={html}
      sandbox="allow-same-origin allow-modals"
      onLoad={handleLoad}
      className={className}
      style={{ border: 'none', width: '100%', display: 'block', ...style }}
    />
  )
}

/**
 * Renders a server-rendered email / print document (invoices, statements,
 * consent forms, …). By default it renders the document straight into the page
 * as a centred print region so the browser prints it natively — robust across
 * Ctrl+P, right-click ▸ Print and File ▸ Print. Pass `isolated` to render it in
 * a sandboxed iframe instead, for when the document is embedded inside an app
 * screen and its global styles must not leak onto the host app.
 */
const PrintEmailContent = (props: PrintEmailContentProps) =>
  props.isolated ? <IsolatedFrame {...props} /> : <DirectContent {...props} />

export default PrintEmailContent
